import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import Skeleton from '@mui/joy/Skeleton'
import Sheet from '@mui/joy/Sheet'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import { OtaHealth, type MobileOtaUpdate, type MobileOtaUpdatesPage, type OtaPlatform } from '@numeo/types-main-mobile'
import { CircleAlert, ExternalLink, RefreshCw, Rocket, Smartphone, Undo2 } from 'lucide-react'
import { mobileOtaUpdatesApi } from 'api/mobileOtaUpdates'
import { StatCard } from '../_shared/StatCard'
import { OtaUpdatesTable } from './OtaUpdatesTable'
import { HEALTH_CHIP, OTA_WORKFLOW_URL, liveReleaseIds } from './otaFormat'

const PLATFORMS: { value: OtaPlatform; label: string }[] = [
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
]

function LiveCardValue({ live }: { live: MobileOtaUpdate[] }) {
    if (live.length === 0) return <>Built-in bundle</>
    if (live.length > 1) return <>{live.length} app versions</>
    return <>v{live[0].appVersion ?? '—'}</>
}

export default function MobileOtaUpdatesPage() {
    const [params, setParams] = useSearchParams()
    const platform: OtaPlatform = params.get('platform') === 'android' ? 'android' : 'ios'
    const [page, setPage] = useState<MobileOtaUpdatesPage | null>(null)
    const [items, setItems] = useState<MobileOtaUpdate[]>([])
    // The 24h totals come from the newest page only, so loading older updates never moves them.
    const [newest, setNewest] = useState<MobileOtaUpdate[]>([])
    const [loading, setLoading] = useState(true)
    const [loadFailed, setLoadFailed] = useState(false)
    const [openId, setOpenId] = useState<string | null>(null)
    const [loadedAt, setLoadedAt] = useState(() => Date.now())

    const load = useCallback(
        async (cursor?: string) => {
            setLoading(true)
            setLoadFailed(false)
            try {
                const next = await mobileOtaUpdatesApi.list(platform, cursor)
                setPage(next)
                setItems((prev) => (cursor ? [...prev, ...next.items] : next.items))
                if (!cursor) setNewest(next.items)
                setLoadedAt(Date.now())
            } catch (error) {
                console.error('Failed to load OTA updates', error)
                setLoadFailed(true)
            } finally {
                setLoading(false)
            }
        },
        [platform]
    )

    useEffect(() => {
        setItems([])
        setNewest([])
        setPage(null)
        setOpenId(null)
        void load()
    }, [load])

    const liveIds = useMemo(() => liveReleaseIds(items), [items])
    const live = items.filter((update) => liveIds.has(update.releaseId))
    const rollbacks24h = newest.reduce((sum, update) => sum + update.last24h.rollbacks, 0)
    const failures24h = newest.reduce((sum, update) => sum + update.last24h.failures, 0)
    const worstLive = live.some((u) => u.health === OtaHealth.BAD)
        ? OtaHealth.BAD
        : live.some((u) => u.health === OtaHealth.WATCH)
          ? OtaHealth.WATCH
          : OtaHealth.OK
    const firstLoad = loading && items.length === 0

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography level="h3">Mobile OTA Updates</Typography>
                        {page ? (
                            <Chip size="sm" variant="soft" color={page.channel === 'production' ? 'danger' : 'primary'}>
                                {page.channel}
                            </Chip>
                        ) : null}
                    </Box>
                    <Typography level="body-sm" textColor="text.tertiary" sx={{ mt: 0.5, maxWidth: 720 }}>
                        Read-only view of Hot Updater (ADR-051). Publishing, rollout and disabling happen in the Mobile App OTA Update workflow.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {page && !loading ? (
                        <Typography level="body-xs" textColor="text.tertiary">
                            Updated at {new Date(loadedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                    ) : null}
                    <Button size="sm" variant="outlined" color="neutral" startDecorator={<RefreshCw size={14} />} loading={loading} onClick={() => void load()}>
                        Refresh
                    </Button>
                    <Button
                        size="sm"
                        variant="outlined"
                        component="a"
                        href={OTA_WORKFLOW_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        endDecorator={<ExternalLink size={14} />}
                    >
                        Open workflow
                    </Button>
                </Box>
            </Box>

            <Tabs
                value={platform}
                onChange={(_event, value) => setParams({ platform: value as OtaPlatform }, { replace: true })}
                sx={{ bgcolor: 'transparent' }}
            >
                <TabList>
                    {PLATFORMS.map((p) => (
                        <Tab key={p.value} value={p.value}>
                            {p.label}
                        </Tab>
                    ))}
                </TabList>
            </Tabs>

            {loadFailed ? (
                <Alert
                    color="danger"
                    endDecorator={
                        <Button size="sm" variant="soft" color="danger" onClick={() => void load()}>
                            Retry
                        </Button>
                    }
                >
                    Could not load OTA updates.
                </Alert>
            ) : null}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {firstLoad ? (
                    [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={74} sx={{ flex: 1, minWidth: 170, borderRadius: 'lg' }} />)
                ) : (
                    <>
                        <StatCard
                            icon={<Rocket size={20} />}
                            label="Live now"
                            hint={live.length === 1 ? HEALTH_CHIP[live[0].health].label : undefined}
                            tone={live.length ? HEALTH_CHIP[worstLive].color : 'neutral'}
                            value={<LiveCardValue live={live} />}
                        />
                        <StatCard icon={<Smartphone size={20} />} label="Active devices" hint="24h" value={page?.activeDevices ?? 0} />
                        <StatCard
                            icon={<Undo2 size={20} />}
                            label="Rollbacks"
                            hint="24h"
                            tone={rollbacks24h && worstLive !== OtaHealth.OK ? HEALTH_CHIP[worstLive].color : 'neutral'}
                            value={rollbacks24h}
                        />
                        <StatCard
                            icon={<CircleAlert size={20} />}
                            label="Failed updates"
                            hint="24h"
                            tone={failures24h ? 'warning' : 'neutral'}
                            value={failures24h}
                        />
                    </>
                )}
            </Box>

            {firstLoad ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[0, 1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="text" level="body-md" />
                    ))}
                </Sheet>
            ) : items.length === 0 && !loadFailed ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 4, textAlign: 'center' }}>
                    <Typography level="title-md">No updates published for {platform === 'ios' ? 'iOS' : 'Android'} yet</Typography>
                    <Typography level="body-sm" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                        Devices run the bundle built into their store app. Publish an update from a release branch with the Mobile App OTA Update workflow.
                    </Typography>
                </Sheet>
            ) : items.length > 0 ? (
                <OtaUpdatesTable
                    items={items}
                    liveIds={liveIds}
                    openId={openId}
                    onToggle={(id) => setOpenId((current) => (current === id ? null : id))}
                    now={loadedAt}
                />
            ) : null}

            {page?.nextCursor && !loading ? (
                <Button variant="outlined" color="neutral" sx={{ alignSelf: 'center' }} onClick={() => void load(page.nextCursor ?? undefined)}>
                    Load older updates
                </Button>
            ) : null}
        </Box>
    )
}
